/**
 * Voice-Leading Lab v1 — pure analyzer for voice-level motion training.
 *
 * Two sub-modes: Voice-Leading Explore (free-pick chord pair + idiom) and
 * Voice-Leading Quiz (10-Q smoothness-discrimination session). Four canonical
 * pairs (I-V, I-IV, V-I, IV-V) under idiom-aware scoring (classical penalises
 * parallel P5/P8; jazz/pop does not).
 *
 * All music-theory decisions are Bowie-locked. The C-major voicing template and
 * transposition live in `voice-leading-tables.ts`.
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §8
 * Bowie scope memo: .claude/agent-memory/bowie/project_voice_leading_lab_v1_scope.md
 */

import type { NoteName } from '@tunasong/schemas'
import { getVoicingPair } from './voice-leading-tables.js'

export const VOICE_LEADING_KEY_POOL = [
  'C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F',
] as const
export type VoiceLeadingKey = (typeof VOICE_LEADING_KEY_POOL)[number]

export const CHORD_PAIRS = ['I-V', 'I-IV', 'V-I', 'IV-V'] as const
export type ChordPair = (typeof CHORD_PAIRS)[number]

export const VOICE_LEADING_IDIOMS = ['classical', 'jazz', 'pop'] as const
export type VoiceLeadingIdiom = (typeof VOICE_LEADING_IDIOMS)[number]

export interface VoicedChord {
  /** Pitches low → high, e.g. ['C3', 'E3', 'G3']. Implicit inversion. */
  pitches: readonly NoteName[]
}

export interface VoicingPair {
  smooth: readonly VoicedChord[]
  awkward: readonly VoicedChord[]
  pair: ChordPair
  key: VoiceLeadingKey
}

// ───── Pitch helpers ─────

const PITCH_TO_SEMITONE: Record<string, number> = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, Fb: 4, 'E#': 5, F: 5,
  'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11, Cb: 11, 'B#': 0,
}

export const splitNote = (note: NoteName): { pitch: string; octave: number } => {
  const match = /^([A-G][#b]?)(-?\d+)$/.exec(note)
  if (!match) {throw new Error(`Invalid note: ${note}`)}
  return { pitch: match[1], octave: Number(match[2]) }
}

const toMidi = (note: NoteName): number => {
  const { pitch, octave } = splitNote(note)
  const semitone = PITCH_TO_SEMITONE[pitch]
  if (semitone === undefined) {throw new Error(`Unknown pitch class: ${pitch}`)}
  return (octave + 1) * 12 + semitone
}

export const semitoneDistance = (from: NoteName, to: NoteName): number => toMidi(to) - toMidi(from)

// ───── Voice motion ─────

export type VoiceMotion = 'common-tone' | 'step' | 'leap'

export const classifyVoiceMotion = (from: NoteName, to: NoteName): VoiceMotion => {
  const semitones = Math.abs(semitoneDistance(from, to))
  if (semitones === 0) {return 'common-tone'}
  if (semitones <= 2) {return 'step'}
  return 'leap'
}

// ───── Parallel-perfect detection ─────

const isPerfectFifthOrOctave = (semitones: number): boolean => {
  const reduced = ((semitones % 12) + 12) % 12
  return reduced === 7 || reduced === 0
}

/** Count voice PAIRS that form a parallel perfect 5th or perfect 8va across A → B. */
export const detectParallelPerfectIntervals = (chordA: VoicedChord, chordB: VoicedChord): number => {
  let count = 0
  const voices = chordA.pitches.length
  for (let i = 0; i < voices; i++) {
    for (let j = i + 1; j < voices; j++) {
      const intervalA = Math.abs(semitoneDistance(chordA.pitches[i], chordA.pitches[j]))
      const intervalB = Math.abs(semitoneDistance(chordB.pitches[i], chordB.pitches[j]))
      const lowerMotion = semitoneDistance(chordA.pitches[i], chordB.pitches[i])
      const upperMotion = semitoneDistance(chordA.pitches[j], chordB.pitches[j])
      if (
        isPerfectFifthOrOctave(intervalA) &&
        intervalA === intervalB &&
        lowerMotion !== 0 &&
        Math.sign(lowerMotion) === Math.sign(upperMotion)
      ) {
        count++
      }
    }
  }
  return count
}

// ───── Cadential-bass exception ─────

const CADENTIAL_PAIRS: ReadonlySet<ChordPair> = new Set<ChordPair>(['V-I'])

export const isCadentialBass = (from: NoteName, to: NoteName, pair: ChordPair): boolean => {
  if (!CADENTIAL_PAIRS.has(pair)) {return false}
  const reduced = ((Math.abs(semitoneDistance(from, to)) % 12) + 12) % 12
  return reduced === 7 || reduced === 5
}

// ───── Scoring ─────

export interface VoiceLeadingScoreBreakdown {
  commonTonesHeld: number
  stepwiseMoves: number
  innerLeaps: number
  outerLeaps: number
  parallelFifthsOrOctaves: number
  contraryOuterMotion: boolean
  total: number
}

const COMMON_TONE_WEIGHT = 3
const STEPWISE_WEIGHT = 2
const INNER_LEAP_PENALTY = -2
const OUTER_LEAP_PENALTY = -1
const PARALLEL_CLASSICAL_PENALTY = -10
const CONTRARY_OUTER_BONUS = 1

export const scoreVoicing = (
  voicing: readonly VoicedChord[],
  opts: { idiom: VoiceLeadingIdiom; pair: ChordPair }
): VoiceLeadingScoreBreakdown => {
  if (voicing.length !== 2) {
    throw new Error('scoreVoicing v1 expects exactly 2 chords')
  }
  const [a, b] = voicing
  const voices = a.pitches.length
  const lastVoice = voices - 1

  let commonTonesHeld = 0
  let stepwiseMoves = 0
  let innerLeaps = 0
  let outerLeaps = 0

  for (let v = 0; v < voices; v++) {
    const motion = classifyVoiceMotion(a.pitches[v], b.pitches[v])
    if (motion === 'common-tone') {
      commonTonesHeld++
      continue
    }
    if (motion === 'step') {
      stepwiseMoves++
      continue
    }
    // leap
    if (v === 0 && isCadentialBass(a.pitches[v], b.pitches[v], opts.pair)) {
      continue // cadential bass — no penalty
    }
    if (v === 0 || v === lastVoice) {
      outerLeaps++
    } else {
      innerLeaps++
    }
  }

  const parallelFifthsOrOctaves = detectParallelPerfectIntervals(a, b)
  const bassMotion = semitoneDistance(a.pitches[0], b.pitches[0])
  const topMotion = semitoneDistance(a.pitches[lastVoice], b.pitches[lastVoice])
  const contraryOuterMotion =
    bassMotion !== 0 && topMotion !== 0 && Math.sign(bassMotion) !== Math.sign(topMotion)

  const parallelPenalty =
    opts.idiom === 'classical' ? PARALLEL_CLASSICAL_PENALTY * parallelFifthsOrOctaves : 0
  const contraryBonus = contraryOuterMotion ? CONTRARY_OUTER_BONUS : 0

  const total =
    COMMON_TONE_WEIGHT * commonTonesHeld +
    STEPWISE_WEIGHT * stepwiseMoves +
    INNER_LEAP_PENALTY * innerLeaps +
    OUTER_LEAP_PENALTY * outerLeaps +
    parallelPenalty +
    contraryBonus

  return {
    commonTonesHeld,
    stepwiseMoves,
    innerLeaps,
    outerLeaps,
    parallelFifthsOrOctaves,
    contraryOuterMotion,
    total,
  }
}

export type ScoreBucket = 'smooth' | 'mixed' | 'awkward'

export const scoreBucket = (total: number): ScoreBucket => {
  if (total >= 5) {return 'smooth'}
  if (total >= 0) {return 'mixed'}
  return 'awkward'
}

// ───── Quiz machinery ─────

export interface VoiceLeadingQuizQuestion {
  voicingPair: VoicingPair
  idiom: VoiceLeadingIdiom
  /** Which presented slot (A or B) holds the smoother voicing. */
  acceptedAnswer: 'A' | 'B'
  presentationOrder: readonly ['smooth', 'awkward'] | readonly ['awkward', 'smooth']
}

export interface VoiceLeadingQuizVerdict {
  correct: boolean
  acceptedSlot: 'A' | 'B'
}

export interface VoiceLeadingQuizResult {
  pair: ChordPair
  correct: boolean
}

export interface VoiceLeadingSessionStats {
  total: number
  correct: number
  byPair: Record<ChordPair, { total: number; correct: number }>
}

const CLASSICAL_POOL: readonly ChordPair[] = ['I-V', 'I-IV', 'V-I', 'IV-V']
const JAZZ_POP_POOL: readonly ChordPair[] = ['I-V', 'I-IV', 'V-I']

export const getValidQuizPairs = (idiom: VoiceLeadingIdiom): readonly ChordPair[] =>
  idiom === 'classical' ? CLASSICAL_POOL : JAZZ_POP_POOL

export const generateVoiceLeadingQuizQuestion = (
  idiom: VoiceLeadingIdiom,
  rng: () => number = Math.random
): VoiceLeadingQuizQuestion => {
  const pairs = getValidQuizPairs(idiom)
  const pair = pairs[Math.floor(rng() * pairs.length)]
  const key = VOICE_LEADING_KEY_POOL[Math.floor(rng() * VOICE_LEADING_KEY_POOL.length)]
  const voicingPair = getVoicingPair(key, pair)
  const smoothFirst = rng() < 0.5
  const presentationOrder: VoiceLeadingQuizQuestion['presentationOrder'] = smoothFirst
    ? ['smooth', 'awkward']
    : ['awkward', 'smooth']
  const acceptedAnswer: 'A' | 'B' = smoothFirst ? 'A' : 'B'
  return { voicingPair, idiom, acceptedAnswer, presentationOrder }
}

export const validateVoiceLeadingQuizAnswer = (
  q: VoiceLeadingQuizQuestion,
  picked: 'A' | 'B'
): VoiceLeadingQuizVerdict => ({
  correct: picked === q.acceptedAnswer,
  acceptedSlot: q.acceptedAnswer,
})

export const computeVoiceLeadingSessionStats = (
  results: readonly VoiceLeadingQuizResult[]
): VoiceLeadingSessionStats => {
  const byPair: Record<ChordPair, { total: number; correct: number }> = {
    'I-V': { total: 0, correct: 0 },
    'I-IV': { total: 0, correct: 0 },
    'V-I': { total: 0, correct: 0 },
    'IV-V': { total: 0, correct: 0 },
  }
  for (const r of results) {
    byPair[r.pair].total++
    if (r.correct) {byPair[r.pair].correct++}
  }
  return {
    total: results.length,
    correct: results.filter(r => r.correct).length,
    byPair,
  }
}
